import React, { useState } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { apiPost } from '../libs/apiHelper'

// Editor del mapper de subida de una template. Guarda en `uploadMapper` vía
// POST /campaign-type { action: 'setUploadMapper' } (solo toca ese campo).
const emptyRow = () => ({ source: '', target: '', required: false, note: '' })

const MapperEditor = ({ template, formatter, onSaved, onCancel }) => {
  const [label, setLabel] = useState(formatter?.label || '')
  const [rows, setRows] = useState(
    formatter?.columns?.length
      ? formatter.columns.map((c) => ({
          source: c.source || '',
          target: c.target || '',
          required: !!c.required,
          note: c.note || ''
        }))
      : [{ source: 'phone', target: 'phone', required: true, note: '' }]
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const update = (i, field, value) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [field]: value } : r)))

  const save = async (uploadMapper) => {
    setSaving(true)
    setError('')
    try {
      const res = await apiPost('/campaign-type', {
        body: { action: 'setUploadMapper', name: template.name, uploadMapper }
      })
      onSaved(res?.uploadMapper ?? null)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Error al guardar')
    }
    setSaving(false)
  }

  const handleSave = () => {
    const columns = rows
      .filter((r) => r.source.trim())
      .map((r) => ({
        source: r.source.trim(),
        target: r.target.trim() || null,
        ...(r.required && { required: true }),
        ...(r.note.trim() && { note: r.note.trim() })
      }))
    if (!columns.some((c) => c.target === 'phone')) {
      setError('Una columna tiene que ir a la variable phone.')
      return
    }
    save({ ...(label.trim() && { label: label.trim() }), columns })
  }

  const input =
    'w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono focus:border-indigo-400 focus:outline-none'

  return (
    <div className="text-left">
      <div className="mb-3 flex items-center gap-2">
        <label className="text-xs font-medium text-gray-700">Nombre</label>
        <input
          className={`${input} max-w-xs font-sans`}
          value={label}
          placeholder="p. ej. CaixaBank Preventivo"
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">Columna del fichero</th>
              <th className="px-2 py-1.5 text-left font-semibold">Variable</th>
              <th className="px-2 py-1.5 text-center font-semibold">Obligatoria</th>
              <th className="px-2 py-1.5 text-left font-semibold">Nota</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="px-2 py-1">
                  <input className={input} value={r.source} placeholder="TELEFONO1"
                    onChange={(e) => update(i, 'source', e.target.value)} />
                </td>
                <td className="px-2 py-1">
                  <input className={input} value={r.target} placeholder="vacío = ignorar"
                    onChange={(e) => update(i, 'target', e.target.value)} />
                </td>
                <td className="px-2 py-1 text-center">
                  <input type="checkbox" checked={r.required}
                    onChange={(e) => update(i, 'required', e.target.checked)} />
                </td>
                <td className="px-2 py-1">
                  <input className={`${input} font-sans`} value={r.note}
                    onChange={(e) => update(i, 'note', e.target.value)} />
                </td>
                <td className="px-2 py-1 text-right">
                  <button type="button" title="Quitar columna"
                    onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Las cabeceras se comparan sin mayúsculas, tildes ni espacios. La variable{' '}
        <span className="font-mono">lang</span> acepta ESP/CAT/es/ca…; el resto se usa
        tal cual en los mensajes como <span className="font-mono">{'{variable}'}</span>.
      </p>
      {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setRows((prev) => [...prev, emptyRow()])}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
          <PlusIcon className="h-4 w-4" /> Añadir columna
        </button>
        <div className="flex-1" />
        {formatter?.fromTemplate && (
          <button type="button" disabled={saving} onClick={() => save(null)}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
            Volver al mapper por defecto
          </button>
        )}
        <button type="button" disabled={saving} onClick={onCancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
          Cancelar
        </button>
        <button type="button" disabled={saving} onClick={handleSave}
          className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Guardando…' : 'Guardar mapper'}
        </button>
      </div>
    </div>
  )
}

export default MapperEditor
